// Where a Test value stands against its lab report (TGCLOUD-1868, plan D3, D5): drawn beside every
// value of the Test that a report gave, while the verification view is open.
//
//   ▣ "Show in the report"     the value is printed at a place in the report. A click selects it
//                              there, scrolls the report to it (switching to the Report tab below
//                              1200px), counts an anchor opened, and rings the value here.
//   ◌ "No source location: …"  the value came from the report but has no place in it to show, and
//                              says why in words (labReportVerificationVocabulary.js). Not a
//                              button: there is nothing to click through to.
//   "Cannot be checked here: the report could not be shown"   every value, while the report pane
//                              cannot draw the report (D5).
// Nothing at all outside a view, or beside a value the report did not give.
//
// `target` is verificationTargets' (`field:lab`, `gene:BRCA2`, `variant:<findingId>`); `fields`
// narrows it to some of its values (one part of a variant); `label` names the value for a screen
// reader, as "Show in the report: Laboratory", and is given translated.
import React from 'react';
import { Box, Button } from '@mui/material';
import CropFreeIcon from '@mui/icons-material/CropFree';
import LocationOffOutlinedIcon from '@mui/icons-material/LocationOffOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { useVerification } from './useVerificationView';
import {
  SHOW_IN_REPORT,
  noSourceLocationText,
  unverifiableText
} from './labReportVerificationVocabulary';

const QUIET_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  px: 0.75,
  py: 0.125,
  borderRadius: 1,
  fontSize: '0.72rem',
  lineHeight: 1.4,
  verticalAlign: 'middle',
  '& svg': { fontSize: '0.95rem' }
};

export default function AnchorMark({
  target,
  fields = null,
  label = null,
  sx
}) {
  const t = useAutoTranslation();
  const view = useVerification();
  const state =
    view && view.active && target
      ? view.anchorStateOf(target, { fields })
      : null;
  if (!state) return null;
  const common = {
    'data-testid': 'anchor-mark',
    'data-anchor-target': target,
    'data-anchor-state': state.state
  };

  if (state.state === 'anchored') {
    return (
      <Button
        {...common}
        size="small"
        variant="text"
        startIcon={<CropFreeIcon />}
        aria-label={label ? `${t(SHOW_IN_REPORT)}: ${label}` : undefined}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          view.showInReport(target, { fields });
        }}
        sx={{
          ml: 0.5,
          py: 0,
          minHeight: 0,
          fontSize: '0.72rem',
          textTransform: 'none',
          verticalAlign: 'middle',
          '& .MuiButton-startIcon': { mr: 0.5 },
          '& .MuiButton-startIcon svg': { fontSize: '0.95rem' },
          ...sx
        }}
      >
        {t(SHOW_IN_REPORT)}
      </Button>
    );
  }

  if (state.state === 'unverifiable') {
    return (
      <Box
        {...common}
        component="span"
        sx={{
          ...QUIET_SX,
          ml: 0.5,
          color: 'warning.dark',
          border: '1px solid',
          borderColor: 'warning.light',
          ...sx
        }}
      >
        <VisibilityOffOutlinedIcon aria-hidden="true" />
        {unverifiableText(t)}
      </Box>
    );
  }

  return (
    <Box
      {...common}
      component="span"
      sx={{
        ...QUIET_SX,
        ml: 0.5,
        color: 'text.secondary',
        fontStyle: 'italic',
        border: '1px dashed',
        borderColor: 'divider',
        ...sx
      }}
    >
      <LocationOffOutlinedIcon aria-hidden="true" />
      {/* reasonText is the view's own words for why (UNANCHORED_REASON_TEXT), never data. */}
      {noSourceLocationText(t(state.reasonText), t)}
    </Box>
  );
}
